import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CornerDownLeft } from 'lucide-react';
import { buildSections, makeIsItemAllowed } from './Sidebar';
import { useRestaurant } from '../context/RestaurantContext';
import { useAuth } from '../context/AuthContext';
import { useT } from '../context/LanguageContext';

/**
 * Type-to-find for the whole navigation — "buffet", "tax", "kitchen" — so a
 * page nobody remembers the section of is one word away instead of three
 * dropdowns deep.
 *
 * It walks the SIDEBAR's own tree (Sidebar.buildSections) through the sidebar's
 * own permission rule, so it can only ever offer pages that user could have
 * clicked to anyway: a second list of what this app contains would drift from
 * the real menu the first time a page moved, and a search that offers a page
 * the server then refuses is worse than no search.
 */

// Enough answers to choose from, few enough to read without scrolling a wall.
const MAX_RESULTS = 8;

/* Every reachable page as one flat row, carrying the trail it was found under
   ("Settings › Notifications › SMS") — two menus both called "Templates" are
   only told apart by where they live. */
function flatten(sections, allowed) {
  const out = [];
  const walk = (item, trail) => {
    if (!allowed(item)) return;
    const here = [...trail, item.label];
    if (Array.isArray(item.children) && item.children.length) {
      item.children.forEach((child) => walk(child, here));
      return;
    }
    // A parked item (greyed in the sidebar) goes nowhere, so it is not an answer.
    if (!item.to || item.disabled) return;
    out.push({
      label: item.label,
      to: item.to,
      icon: item.icon,
      // The trail WITHOUT the leaf's own label, which is shown on its own line.
      path: here.slice(0, -1).join(' › '),
      hay: here.join(' ').toLowerCase(),
    });
  };
  sections.forEach((section) => {
    (section.items || []).forEach((item) => walk(item, section.title ? [section.title] : []));
  });
  return out;
}

/* Ranked, not just filtered: a label that STARTS with what was typed is what
   the person meant, a label that merely contains it is second, and a match
   only in the trail ("Settings" finding SMS) comes last. */
function search(rows, raw) {
  const q = raw.trim().toLowerCase();
  if (!q) return [];
  const scored = [];
  for (const row of rows) {
    const label = row.label.toLowerCase();
    let score;
    if (label.startsWith(q)) score = 0;
    else if (label.includes(q)) score = 1;
    else if (row.hay.includes(q)) score = 2;
    else continue;
    scored.push({ row, score });
  }
  scored.sort((a, b) => a.score - b.score || a.row.label.localeCompare(b.row.label));
  return scored.slice(0, MAX_RESULTS).map((s) => s.row);
}

export default function MenuSearch() {
  const navigate = useNavigate();
  const { t } = useT();
  const { restaurantId } = useRestaurant();
  const { hasPermission, isPermissionGated, role, user } = useAuth();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const boxRef = useRef(null);
  const inputRef = useRef(null);

  /* Rebuilt only when the menu itself could have changed — the tree is the
     same object for every keystroke, so typing filters rather than rebuilds. */
  const rows = useMemo(
    () => flatten(buildSections(restaurantId, t),
      makeIsItemAllowed({ role, user, hasPermission, isPermissionGated })),
    [restaurantId, t, role, user, hasPermission, isPermissionGated],
  );
  const results = useMemo(() => search(rows, query), [rows, query]);

  const close = useCallback(() => { setOpen(false); setActive(0); }, []);

  const go = useCallback((row) => {
    if (!row) return;
    setQuery('');
    close();
    inputRef.current?.blur();
    navigate(row.to);
  }, [close, navigate]);

  // Clicking anywhere else puts the list away; the input keeps what was typed.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) close();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, close]);

  /* Ctrl/⌘-K from anywhere, the shortcut every search box of this shape has.
     Ignored while typing in another field, so it cannot steal a real keystroke. */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'k' || !(e.metaKey || e.ctrlKey)) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { close(); inputRef.current?.blur(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(results[active] || results[0]);
    }
  };

  const showList = open && query.trim().length > 0;

  return (
    <div ref={boxRef} className="relative hidden lg:block">
      <Search
        size={15}
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
      />
      <input
        ref={inputRef}
        type="search"
        role="combobox"
        aria-expanded={showList}
        aria-controls="menu-search-results"
        aria-autocomplete="list"
        aria-label={t('search.menus')}
        placeholder={t('search.menus')}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActive(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="h-9 w-56 xl:w-64 rounded-full border border-input bg-background pl-9 pr-3
                   text-sm text-foreground placeholder:text-muted-foreground
                   focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring
                   [&::-webkit-search-cancel-button]:appearance-none"
      />

      {showList && (
        <div
          id="menu-search-results"
          role="listbox"
          className="absolute right-0 z-50 mt-1 w-80 overflow-hidden rounded-lg border border-border
                     bg-popover shadow-lg"
        >
          {results.length === 0 ? (
            <p className="px-3 py-3 text-sm text-muted-foreground">
              {t('search.noMatch', { q: query.trim() })}
            </p>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {results.map((row, i) => {
                const Icon = row.icon;
                return (
                  <li key={`${row.to}-${row.label}`}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={i === active}
                      /* Pointer-down rather than click: the input's blur would
                         otherwise close the list before the click landed. */
                      onMouseDown={(e) => { e.preventDefault(); go(row); }}
                      onMouseEnter={() => setActive(i)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                        i === active ? 'bg-accent' : ''
                      }`}
                    >
                      {Icon ? <Icon size={15} className="shrink-0 text-muted-foreground" />
                        : <span className="w-[15px] shrink-0" aria-hidden="true" />}
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm text-foreground">{row.label}</span>
                        {row.path && (
                          <span className="block truncate text-xs text-muted-foreground">{row.path}</span>
                        )}
                      </span>
                      {i === active && (
                        <CornerDownLeft size={13} aria-hidden="true" className="shrink-0 text-muted-foreground" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
